import { logImage, shortImageUrl } from './image-logger.js';

/** Stable React key + display URL for outgoing images (survives WS reconcile). */
const displayUrlByKey = new Map<string, string>();
const blobByClientId = new Map<string, string>();
const clientIdByServerId = new Map<string, string>();
const clientIdByImageFilename = new Map<string, string>();

function imageFilename(url?: string): string | undefined {
  if (!url) return undefined;
  try {
    return new URL(url).pathname.split('/').pop() ?? undefined;
  } catch {
    const parts = url.split('/');
    return parts[parts.length - 1];
  }
}

export function linkClientIdToImageUrl(clientId: string, url: string): void {
  const name = imageFilename(url);
  if (name) {
    clientIdByImageFilename.set(name, clientId);
    logImage('cache', 'link clientId to image filename', { clientId, name });
  }
}

export function imageMessageKey(clientId?: string, id?: string): string {
  return clientId ?? id ?? '';
}

/** Stable list key: keep clientId after server assigns numeric id. */
export function resolveMessageReactKey(message: {
  id: string;
  clientId?: string;
  mediaUrl?: string;
  attachment?: { url?: string };
}): string {
  if (message.clientId) {
    clientIdByServerId.set(String(message.id), message.clientId);
    return message.clientId;
  }

  const bound = clientIdByServerId.get(String(message.id));
  if (bound) return bound;

  const mediaUrl = message.mediaUrl ?? message.attachment?.url;
  const filename = imageFilename(mediaUrl);
  // YouTube/Vimeo watch URLs share pathname "watch" — only use filename
  // fallback for real image files (unique path segment).
  const looksLikeImageFile =
    filename != null && /\.(jpe?g|png|gif|webp|avif|svg)$/i.test(filename);
  if (looksLikeImageFile) {
    const byFile = clientIdByImageFilename.get(filename);
    if (byFile) {
      clientIdByServerId.set(String(message.id), byFile);
      logImage('cache', 'resolve react key by filename', {
        id: message.id,
        clientId: byFile,
        filename,
      });
      return byFile;
    }
  }

  return String(message.id);
}

export function registerImageBlobPreview(clientId: string, blobUrl: string): void {
  blobByClientId.set(clientId, blobUrl);
  displayUrlByKey.set(clientId, blobUrl);
  logImage('cache', 'register blob preview', {
    clientId,
    url: shortImageUrl(blobUrl),
  });
}

export function registerUploadedImageUrl(clientId: string, serverUrl: string): void {
  linkClientIdToImageUrl(clientId, serverUrl);
  displayUrlByKey.set(clientId, serverUrl);
}

export function getCachedDisplayUrl(key: string): string | undefined {
  return displayUrlByKey.get(key);
}

export function resolveImageDisplayUrl(
  clientId: string | undefined,
  id: string | undefined,
  serverUrl: string | undefined,
): string | undefined {
  const key = imageMessageKey(clientId, id);
  if (!key) return serverUrl;

  const cached = displayUrlByKey.get(key);
  if (cached) return cached;

  if (serverUrl) {
    displayUrlByKey.set(key, serverUrl);
    return serverUrl;
  }

  return undefined;
}

/** Preload HTTPS URL, then swap display cache (single visual transition). */
export function preloadServerImageUrl(
  clientId: string,
  serverUrl: string,
): Promise<void> {
  const blob = blobByClientId.get(clientId);
  logImage('cache', 'preload server url', {
    clientId,
    from: shortImageUrl(blob),
    to: shortImageUrl(serverUrl),
  });

  return new Promise((resolve) => {
    if (typeof Image === 'undefined') {
      displayUrlByKey.set(clientId, serverUrl);
      resolve();
      return;
    }

    const img = new Image();
    img.onload = () => {
      displayUrlByKey.set(clientId, serverUrl);
      registerUploadedImageUrl(clientId, serverUrl);
      logImage('cache', 'preload complete → swap display url', {
        clientId,
        url: shortImageUrl(serverUrl),
      });
      if (blob?.startsWith('blob:')) {
        URL.revokeObjectURL(blob);
        blobByClientId.delete(clientId);
      }
      resolve();
    };
    img.onerror = () => {
      logImage(
        'cache',
        'preload failed, keeping current display url',
        { clientId, url: shortImageUrl(serverUrl) },
        'warn',
      );
      displayUrlByKey.set(clientId, serverUrl);
      resolve();
    };
    img.src = serverUrl;
  });
}

export function bindServerIdToClientKey(
  clientId: string,
  serverId: string,
  serverUrl?: string,
): void {
  clientIdByServerId.set(serverId, clientId);
  const display = displayUrlByKey.get(clientId);
  if (display) {
    displayUrlByKey.set(serverId, display);
    logImage('cache', 'bind server id to display url', {
      clientId,
      serverId,
      url: shortImageUrl(display),
    });
  } else if (serverUrl) {
    displayUrlByKey.set(serverId, serverUrl);
    displayUrlByKey.set(clientId, serverUrl);
  }
}

/** Previously bound server id → optimistic clientId (for reconcile). */
export function getClientIdBoundToServerId(serverId: string): string | undefined {
  return clientIdByServerId.get(serverId);
}

export function clearImageDisplayCache(): void {
  for (const blob of blobByClientId.values()) {
    if (blob.startsWith('blob:')) URL.revokeObjectURL(blob);
  }
  blobByClientId.clear();
  displayUrlByKey.clear();
  clientIdByServerId.clear();
  clientIdByImageFilename.clear();
}
